import React, { useMemo } from "react";
import { PerformanceGameRewardModes } from "kadow-jetpack-model";
import { tt } from "../../../../utils/i18n/i18n";
import { useJetpackConfigSelector } from "../../../../redux/jetpackConfig";
import NiceModal from "@ebay/nice-modal-react";
import CreateAnimationModal, {
  CreateAnimationStep
} from "../../../../components/modal/ConfigAnimationModal/CreateAnimationModal";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import GameConfigUpsell from "../UpsellComponents/GameConfigUpsell";
import KC_Padder from "../../../../designsystem/KC_Padder/KC_Padder";
import EventSuggestion from "./EventSuggestions";
import GameAndEventSection from "./components/GameAndEventSection";

export default function GamesConfigurator() {
  const jpConfig = useJetpackConfigSelector();
  const { accountToDisplay } = useAccountToDisplaySelector();

  const canUseGamesOfChance = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.gc;
  }, [accountToDisplay]);

  const canUsePerformanceGames = useMemo(() => {
    const monthlyLotteryEnabledOrPhysicalReward =
      !jpConfig?.dml || jpConfig?.pgrm === PerformanceGameRewardModes.Prize;
    return (
      monthlyLotteryEnabledOrPhysicalReward &&
      !!accountToDisplay?.saasFeatures?.pg
    );
  }, [jpConfig?.dml, jpConfig?.pgrm, accountToDisplay?.saasFeatures]);

  const displayEventSuggestion = useMemo(() => {
    return !!accountToDisplay?.saasFeatures?.pg;
  }, [accountToDisplay?.saasFeatures]);

  const showCreateAnimationModal = async (step: CreateAnimationStep) => {
    await NiceModal.show(CreateAnimationModal, {
      title: tt("create_new_animation"),
      step,
      includePerformanceGames: canUsePerformanceGames,
      includeGamesOfChance: canUseGamesOfChance
    });
  };

  const UPSELL = useMemo(() => {
    if (!canUseGamesOfChance || !canUsePerformanceGames) {
      return (
        <GameConfigUpsell
          includeGamesOfChance={!canUseGamesOfChance}
          includePerformanceGames={!canUsePerformanceGames}
          includeSkins={true}
          config={jpConfig}
        />
      );
    }

    return null;
  }, [canUseGamesOfChance, canUsePerformanceGames, jpConfig]);

  const ephemeralEvents = useMemo(() => {
    if (!jpConfig?.activeEvents?.length) {
      return [];
    }
    return jpConfig?.activeEvents?.filter((ev) => ev.sts > -1 && ev.ets > -1);
  }, [jpConfig?.activeEvents]);

  const permanentEvents = useMemo(() => {
    if (!jpConfig?.activeEvents?.length) {
      return [];
    }
    return jpConfig?.activeEvents?.filter(
      (ev) => ev.sts === -1 && ev.ets === -1
    );
  }, [jpConfig?.activeEvents]);

  return (
    <div className="dashboard-v2">
      <GameAndEventSection
        games={jpConfig?.games || []}
        permanentEvent={permanentEvents}
        ephemeralEvents={ephemeralEvents}
        canUseGamesOfChance={canUseGamesOfChance}
        canUsePerformanceGames={canUsePerformanceGames}
        showCreateAnimationModal={showCreateAnimationModal}
      />
      {displayEventSuggestion && (
        <div className="px-4">
          <EventSuggestion />
        </div>
      )}
      <div className="mt-4 px-4">{UPSELL}</div>
      <KC_Padder />
    </div>
  );
}
